import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import styles from './CompositionRowField.module.css';
import ThemeContext from 'config/context/themeContext';
import dataTypesConfig from '../../constants/dataTypes.json';
import { typeConverters } from '../../utils/typeConverters';
import { valiedateNameField } from 'utils/validations';

const CompositionRowField = ({ type, value, onChange, isEditable = false, isObject = false, isArray = false }) => {
    const { dark } = useContext(ThemeContext);
    const [localValue, setLocalValue] = useState('');
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    const arrayPlaceholder = useMemo(() => {
        switch (type) {
            case dataTypesConfig.supportedTypes.STRING:
            case dataTypesConfig.supportedTypes.NAME:
                return '["text", "text2"]';
            case dataTypesConfig.supportedTypes.BOOL:
                return '[true, false]';
            case dataTypesConfig.supportedTypes.TIME_POINT:
                return '["2024-01-01T00:00:00Z", "2024-06-01T12:30:00Z"]';
            case dataTypesConfig.supportedTypes.UINT128_T:
            case dataTypesConfig.supportedTypes.UINT64_T:
            case dataTypesConfig.supportedTypes.UINT32_T:
            case dataTypesConfig.supportedTypes.UINT16_T:
            case dataTypesConfig.supportedTypes.UINT8_T:
            case dataTypesConfig.supportedTypes.INT64_T:
            case dataTypesConfig.supportedTypes.INT32_T:
            case dataTypesConfig.supportedTypes.INT16_T:
            case dataTypesConfig.supportedTypes.INT8_T:
                return '[1, 2, 3]';
            case dataTypesConfig.supportedTypes.DOUBLE:
            case dataTypesConfig.supportedTypes.FLOAT:
                return '[1.23, 4.56]';
            default:
                return '["value1", "value2"]';
        }
    }, [type]);

    // Get converter for current type
    const converter = useMemo(() => 
        typeConverters[type] || typeConverters[dataTypesConfig.supportedTypes.STRING], 
        [type]
    );

    // Sync with external value changes
    useEffect(() => {
        if (value === undefined) return;
        if (isObject || isArray) {
            try {
                const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
                setLocalValue(text);
            } catch (_) {
                setLocalValue('');
            }
        } else {
            setLocalValue(converter.toDisplay(value));
        }
    }, [value, converter, isObject, isArray]);

    const handleChange = (newValue) => {
        if (!onChange || !isEditable) return;
        if (type === dataTypesConfig.supportedTypes.NAME) {
            // Allow empty to propagate, validate non-empty values
            if (newValue === '' || valiedateNameField(newValue)) {
                setLocalValue(newValue);
                onChange(converter.fromDisplay(newValue));
            }
            return;
        }
        setLocalValue(newValue);
        onChange(converter.fromDisplay(newValue));
    };

    // Render different input types
    const renderInput = () => {
        if (isObject) {
            return (
                <>
                    <button type="button" className={styles.json_trigger} onClick={() => setOpen(!open)} disabled={!isEditable}>
                        {localValue ? 'Edit object' : 'Add object'}
                    </button>
                    {open && (
                        <div className={styles.json_popover} ref={popoverRef}>
                            <textarea 
                                className={styles.json_textarea}
                                value={localValue}
                                onChange={(e) => setLocalValue(e.target.value)}
                                readOnly={!isEditable}
                                placeholder={'{"key": "value"}'}
                            />
                            <div className={styles.json_actions}>
                                <button className={styles.ghost} type="button" onClick={() => setOpen(false)}>Cancel</button>
                                <button className={styles.primary} type="button" onClick={() => {
                                    try {
                                        const parsed = localValue ? JSON.parse(localValue) : null;
                                        if (onChange && isEditable) onChange(parsed);
                                        setOpen(false);
                                    } catch (_) {
                                        // Try a lenient parse: quote keys, single->double quotes, remove trailing commas
                                        try {
                                            let text = (localValue || '').trim();
                                            // Replace single-quoted strings with double quotes
                                            text = text.replace(/'([^']*)'/g, '"$1"');
                                            // Quote unquoted object keys
                                            text = text.replace(/([\{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":');
                                            // Remove trailing commas before } or ]
                                            text = text.replace(/,\s*([}\]])/g, '$1');
                                            const parsedLenient = text ? JSON.parse(text) : null;
                                            if (onChange && isEditable) onChange(parsedLenient);
                                            setLocalValue(text);
                                            setOpen(false);
                                        } catch (e2) {
                                            // still invalid; keep popover open for correction
                                        }
                                    }
                                }}>Save</button>
                            </div>
                        </div>
                    )}
                </>
            );
        }
        if (isArray) {
            return (
                <>
                    <button type="button" className={styles.json_trigger} onClick={() => setOpen(!open)} disabled={!isEditable}>
                        {localValue ? 'Edit list' : 'Add list'}
                    </button>
                    {open && (
                        <div className={styles.json_popover} ref={popoverRef}>
                            <textarea 
                                className={styles.json_textarea}
                                value={localValue}
                                onChange={(e) => setLocalValue(e.target.value)}
                                readOnly={!isEditable}
                                placeholder={arrayPlaceholder}
                            />
                            <div className={styles.json_actions}>
                                <button className={styles.ghost} type="button" onClick={() => setOpen(false)}>Cancel</button>
                                <button className={styles.primary} type="button" onClick={() => {
                                    try {
                                        let text = (localValue || '').trim();
                                        // If user typed comma-separated without brackets, wrap it
                                        const looksLikeCSV = text && !text.startsWith('[') && text.includes(',');
                                        if (looksLikeCSV) {
                                            text = `[${text}]`;
                                        }
                                        // Lenient: single quotes to double
                                        text = text.replace(/'([^']*)'/g, '"$1"');
                                        // Remove trailing commas
                                        text = text.replace(/,\s*([}\]])/g, '$1');
                                        const parsed = text ? JSON.parse(text) : [];
                                        if (Array.isArray(parsed)) {
                                            if (onChange && isEditable) onChange(parsed);
                                            setLocalValue(text);
                                            setOpen(false);
                                        }
                                    } catch (_) {
                                        // keep open for correction
                                    }
                                }}>Save</button>
                            </div>
                        </div>
                    )}
                </>
            );
        }
        switch (type) {
            case dataTypesConfig.supportedTypes.STRING:
            case dataTypesConfig.supportedTypes.NAME:
                return (
                    <input 
                        type="text" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                    />
                );

            case dataTypesConfig.supportedTypes.UINT128_T:
            case dataTypesConfig.supportedTypes.UINT64_T:
            case dataTypesConfig.supportedTypes.UINT32_T:
            case dataTypesConfig.supportedTypes.UINT16_T:
            case dataTypesConfig.supportedTypes.UINT8_T:
            case dataTypesConfig.supportedTypes.INT64_T:
            case dataTypesConfig.supportedTypes.INT32_T:
            case dataTypesConfig.supportedTypes.INT16_T:
            case dataTypesConfig.supportedTypes.INT8_T:
            case dataTypesConfig.supportedTypes.DOUBLE:
            case dataTypesConfig.supportedTypes.FLOAT:
                return (
                    <input 
                        type="number" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                        step={type === dataTypesConfig.supportedTypes.DOUBLE || type === dataTypesConfig.supportedTypes.FLOAT ? "any" : "1"}
                    />
                );

            case dataTypesConfig.supportedTypes.TIME_POINT:
                return (
                    <input 
                        type="datetime-local" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                    />
                );

            case dataTypesConfig.supportedTypes.BOOL:
                return (
                    <div className={styles.radio_thumb}>
                        <input
                            type="checkbox"
                            checked={localValue === 'true'}
                            onChange={(e) => handleChange(e.target.checked.toString())}
                            disabled={!isEditable}
                        />
                    </div>
                );

            default:
                return (
                    <input 
                        type="text" 
                        value={localValue} 
                        onChange={(e) => handleChange(e.target.value)}
                        readOnly={!isEditable}
                        placeholder={type}
                    />
                );
        }
    };

    if (isObject) {
        return (
            <div className={`${styles.composition_row_field} ${dark ? styles.dark : ''}`}>
                <div className={styles.field_holder}>
                    {renderInput()}
                </div>
            </div>
        );
    }

    if (!typeConverters[type]) {
        return (
            <div className={`${styles.composition_row_field} ${dark ? styles.dark : ''}`}>
                <div className={styles.field_holder}>
                    <span>Unsupported type: {type}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.composition_row_field} ${dark ? styles.dark : ''}`}>
            <div className={styles.field_holder}>
                {renderInput()}
            </div>
        </div>
    );
}

export default CompositionRowField;